/**
 * Jumpstart-styled form inputs for react-admin
 *
 * These components wrap the authentic Jumpstart field components
 * (TextField, PasswordField, SelectField, CheckboxField) and bind the
 * inner native control to react-admin's form state via `useInput`.
 */

import React, { useMemo, useRef, useState } from 'react';
import { useInput, InputHelperText, useResourceContext, useTranslate } from 'react-admin';
import { useFormState } from 'react-hook-form';
import {
    TextField as JsTextField,
    PasswordField as JsPasswordField,
    SelectField as JsSelectField,
    CheckboxField as JsCheckboxField,
    ButtonField as JsButtonField,
    FileUpload as JsFileUpload,
    Multiselect2,
    Multiselect2Input,
    Multiselect2OptionItem,
    Multiselect2OptionList,
    Multiselect2SelectedOptionList,
    PillDismissibleField,
    PillGroup,
    PillTextField,
    SrOnly,
    WkIconChevronDownSprite,
    WkIconChevronUpSprite,
    WkIconCloseCircleSprite,
    WkIconFilledCautionSprite,
    WkIconFilledCheckCircleSprite,
    WkIconFilledCheckSprite,
    WkIconSpinnerSprite,
} from '@wk/components-v3-react16';
import { Box } from '@mui/material';
import '../styles/jumpstart-forms.css';

type PasswordFieldMode = 'text' | 'password';

interface BaseInputProps {
    source: string;
    label?: string;
    helperText?: string;
    validate?: any;
    disabled?: boolean;
    defaultValue?: any;
    type?: string;
    fullWidth?: boolean;
    multiline?: boolean;
    rows?: number;
}

const helperSx = { mt: 0.5, fontSize: '0.8125rem', color: 'var(--cg3-color-gray-625, #353535)' } as const;

// Field wrapper sizing. Inputs fill the full width of the form column.
const wrapSx = (fullWidth?: boolean) => ({
    mb: 2.5,
    width: '100%',
    maxWidth: '100%',
});

/**
 * Jumpstart TextField bound to react-admin form state.
 * Renders a textarea when `multiline` is set.
 */
export const JumpstartTextInput = (props: BaseInputProps) => {
    const { label, helperText, disabled, type = 'text', multiline, rows = 3, fullWidth } = props;
    const {
        field,
        fieldState: { error, invalid },
        isRequired,
        id,
    } = useInput(props);

    const labelId = `${id}-label`;

    return (
        <Box sx={wrapSx(fullWidth)} className="jumpstart-input">
            <JsTextField
                label={label as string}
                labelFor={id}
                labelId={labelId}
                indicator={isRequired ? 'required' : 'none'}
                status={invalid ? 'error' : 'none'}
                errors={invalid && error?.message ? [error.message] : []}
            >
                {multiline ? (
                    <textarea
                        id={id}
                        name={field.name}
                        rows={rows}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={disabled}
                        autoComplete="off"
                    />
                ) : (
                    <input
                        id={id}
                        type={type}
                        name={field.name}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={disabled}
                        aria-autocomplete="none"
                        autoComplete="off"
                    />
                )}
            </JsTextField>
            {helperText && !invalid && (
                <Box sx={helperSx}>
                    <InputHelperText helperText={helperText} />
                </Box>
            )}
        </Box>
    );
};

/**
 * Jumpstart PasswordField bound to react-admin form state.
 */
export const JumpstartPasswordInput = (props: BaseInputProps) => {
    const { label, helperText, disabled, fullWidth } = props;
    const {
        field,
        fieldState: { error, invalid },
        isRequired,
        id,
    } = useInput(props);
    const [mode, setMode] = useState<PasswordFieldMode>('password');

    const labelId = `${id}-label`;

    return (
        <Box sx={wrapSx(fullWidth)} className="jumpstart-input">
            <JsPasswordField
                label={label as string}
                labelFor={id}
                labelId={labelId}
                indicator={isRequired ? 'required' : 'none'}
                status={invalid ? 'error' : 'none'}
                errors={invalid && error?.message ? [error.message] : []}
                mode={mode}
                onModeChange={({ mode: nextMode }: { mode: PasswordFieldMode }) => setMode(nextMode)}
            >
                <input
                    id={id}
                    type={mode}
                    name={field.name}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={disabled}
                    autoComplete="new-password"
                />
            </JsPasswordField>
            {helperText && !invalid && (
                <Box sx={helperSx}>
                    <InputHelperText helperText={helperText} />
                </Box>
            )}
        </Box>
    );
};

interface SelectChoice {
    id: string | number;
    name: string;
}

interface JumpstartSelectInputProps extends BaseInputProps {
    choices?: Array<Record<string, any>>;
    optionText?: string;
    optionValue?: string;
    emptyText?: string;
}

/**
 * Jumpstart SelectField bound to react-admin form state.
 */
export const JumpstartSelectInput = (props: JumpstartSelectInputProps) => {
    const {
        label,
        helperText,
        disabled,
        choices = [],
        optionText = 'name',
        optionValue = 'id',
        emptyText = '',
        fullWidth,
    } = props;
    const {
        field,
        fieldState: { error, invalid },
        isRequired,
        id,
    } = useInput(props);

    const labelId = `${id}-label`;

    return (
        <Box sx={wrapSx(fullWidth)} className="jumpstart-input">
            <JsSelectField
                label={label as string}
                labelFor={id}
                labelId={labelId}
                indicator={isRequired ? 'required' : 'none'}
                status={invalid ? 'error' : 'none'}
                errors={invalid && error?.message ? [error.message] : []}
            >
                <select
                    id={id}
                    name={field.name}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={disabled}
                >
                    <option value="">{emptyText || '\u00A0'}</option>
                    {choices.map((choice: any) => (
                        <option key={choice[optionValue]} value={choice[optionValue]}>
                            {choice[optionText]}
                        </option>
                    ))}
                </select>
            </JsSelectField>
            {helperText && !invalid && (
                <Box sx={helperSx}>
                    <InputHelperText helperText={helperText} />
                </Box>
            )}
        </Box>
    );
};

/**
 * Jumpstart CheckboxField bound to react-admin form state.
 */
export const JumpstartCheckboxInput = (props: BaseInputProps) => {
    const { label, source, disabled } = props;
    const {
        field,
        fieldState: { error, invalid },
        id,
    } = useInput(props);
    const resource = useResourceContext();
    const translate = useTranslate();

    // Resolve a plain-text label for the Jumpstart CheckboxField `label` prop.
    let labelText = '';
    if (typeof label === 'string') {
        labelText = label;
    } else if (label !== false) {
        const key = `resources.${resource}.fields.${source}`;
        const humanized = source
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
        labelText = translate(key, { _: humanized });
    }

    return (
        <Box sx={{ mb: 2, maxWidth: 480 }} className="jumpstart-input">
            <JsCheckboxField label={labelText} labelFor={id}>
                <input
                    id={id}
                    type="checkbox"
                    name={field.name}
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    onBlur={field.onBlur}
                    disabled={disabled}
                    aria-disabled={disabled ? 'true' : undefined}
                />
            </JsCheckboxField>
            {invalid && error?.message && (
                <Box sx={{ mt: 0.5, fontSize: '0.8125rem', color: 'var(--cg3-color-red-500, #c91919)' }}>
                    {error.message}
                </Box>
            )}
        </Box>
    );
};

/**
 * Pass-through wrapper kept for backward compatibility.
 * No longer needed for styling since inputs are now authentic Jumpstart fields.
 */
export const JumpstartFormWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return <Box className="jumpstart-form-wrapper">{children}</Box>;
};

/* ====================================================================== */
/* Multi-select (SelectArrayInput replacement)                            */
/* ====================================================================== */

interface JumpstartSelectArrayInputProps extends BaseInputProps {
    choices?: SelectChoice[];
    optionText?: string;
    optionValue?: string;
    size?: number;
}

/**
 * Jumpstart multi-select bound to react-admin form state.
 * Renders a native `<select multiple>` inside a Jumpstart SelectField.
 */
export const JumpstartSelectArrayInput = (props: JumpstartSelectArrayInputProps) => {
    const {
        label,
        helperText,
        disabled,
        choices = [],
        optionText = 'name',
        optionValue = 'id',
        size = 6,
        fullWidth,
    } = props;
    const {
        field,
        fieldState: { error, invalid },
        isRequired,
        id,
    } = useInput(props);

    // Jumpstart Multiselect2 internally uses querySelector on ids/selectors.
    // react-admin ids may contain ':' (e.g. ":rdb:"), which is invalid in
    // CSS selectors without escaping and breaks popup target lookup.
    const safeHtmlId = String(id || props.source || 'jumpstart-multi')
        .replace(/[^a-zA-Z0-9_-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'jumpstart-multi';

    const [inputValue, setInputValue] = useState('');
    const selectedValues: string[] = Array.isArray(field.value)
        ? field.value.map((v: any) => String(v))
        : [];

    const normalizedChoices = useMemo(
        () => choices.map((choice: any) => ({
            value: String(choice[optionValue]),
            label: String(choice[optionText]),
        })),
        [choices, optionText, optionValue],
    );

    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    const selectedOptions = useMemo(
        () => normalizedChoices.filter((choice) => selectedSet.has(choice.value)),
        [normalizedChoices, selectedSet],
    );

    const filteredOptions = useMemo(() => {
        const q = inputValue.trim().toLowerCase();
        if (!q) return normalizedChoices;
        return normalizedChoices.filter((choice) => choice.label.toLowerCase().includes(q));
    }, [normalizedChoices, inputValue]);

    const toggleOption = (value: string) => {
        const next = new Set(selectedValues);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        field.onChange(Array.from(next));
        setInputValue('');
    };

    const clearAll = () => {
        field.onChange([]);
        setInputValue('');
    };

    return (
        <Box sx={wrapSx(fullWidth)} className="jumpstart-input jumpstart-input--multi">
            <Multiselect2
                htmlId={safeHtmlId}
                label={label as string}
                indicator={isRequired ? 'required' : 'none'}
                status={invalid ? 'error' : 'none'}
                errors={invalid && error?.message ? [error.message] : []}
                isDisabled={disabled}
                inputValue={inputValue}
                inputPlaceholder="Select scopes"
                onInputChange={({ inputValue: next }: { inputValue: string }) => setInputValue(next)}
                onInputClear={() => setInputValue('')}
                onOptionClick={({ optionId }: { optionId: string }) => toggleOption(optionId)}
            >
                {selectedOptions.length > 0 && (
                    <PillGroup
                        groupId={`${safeHtmlId}-pill-group`}
                        srLabelledBy={`${safeHtmlId}-pill-group-heading`}
                        initLinesCount={400}
                        isExpanded={true}
                        flowClass="cg3-mar-b-0_5"
                    >
                        <SrOnly slot="heading">
                            <h3 id={`${safeHtmlId}-pill-group-heading`}>Selected options</h3>
                        </SrOnly>

                        {selectedOptions.map((option) => (
                            <PillDismissibleField key={option.value} slot="pillItem">
                                <button
                                    type="button"
                                    onClick={() => toggleOption(option.value)}
                                >
                                    <SrOnly>Remove</SrOnly>
                                    {option.label}
                                </button>
                            </PillDismissibleField>
                        ))}

                        {selectedOptions.length > 1 && (
                            <PillTextField slot="pillClearButton">
                                <button type="button" onClick={clearAll}>
                                    Clear all
                                </button>
                            </PillTextField>
                        )}
                    </PillGroup>
                )}

                <Multiselect2SelectedOptionList labels={selectedOptions.map((option) => option.label)}>
                    <Multiselect2Input />
                </Multiselect2SelectedOptionList>

                <Multiselect2OptionList>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option) => (
                            <Multiselect2OptionItem
                                key={option.value}
                                htmlId={option.value}
                                isSelected={selectedSet.has(option.value)}
                            >
                                {option.label}
                            </Multiselect2OptionItem>
                        ))
                    ) : (
                        <Multiselect2OptionItem htmlId={`${safeHtmlId}-no-match`} isBlank={true}>
                            No results found.
                        </Multiselect2OptionItem>
                    )}
                </Multiselect2OptionList>
            </Multiselect2>

            <WkIconChevronUpSprite />
            <WkIconChevronDownSprite />
            <WkIconCloseCircleSprite />
            <WkIconSpinnerSprite />
            <WkIconFilledCheckCircleSprite />
            <WkIconFilledCautionSprite />
            <WkIconFilledCheckSprite />

            <input
                type="hidden"
                name={field.name}
                value={selectedValues.join(',')}
                onBlur={field.onBlur}
                readOnly
            />
            {helperText && !invalid && (
                <Box sx={helperSx}>
                    <InputHelperText helperText={helperText} />
                </Box>
            )}
        </Box>
    );
};

/* ====================================================================== */
/* List filter inputs (react-admin filters array)                         */
/* ====================================================================== */

interface JumpstartSearchInputProps {
    source: string;
    label?: string;
    placeholder?: string;
    alwaysOn?: boolean;
}

/**
 * Compact Jumpstart text input for react-admin List `filters`.
 * Binds to the filter form via `useInput`; label hidden, placeholder shown.
 */
export const JumpstartSearchInput = (props: JumpstartSearchInputProps) => {
    const { label, placeholder } = props;
    const { field, id } = useInput(props);

    return (
        <Box className="jumpstart-filter" sx={{ minWidth: 200, mb: 0 }}>
            <JsTextField label={label || ''} labelFor={id} labelId={`${id}-label`}>
                <input
                    id={id}
                    type="search"
                    name={field.name}
                    placeholder={placeholder}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    autoComplete="off"
                />
            </JsTextField>
        </Box>
    );
};

/* ====================================================================== */
/* Standalone (controlled) filter inputs for custom filter panels         */
/* ====================================================================== */

interface JumpstartFilterTextProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    onEnter?: () => void;
    minWidth?: number;
    type?: string;
    fullWidth?: boolean;
    errorText?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    placeholder?: string;
}

/**
 * Standalone controlled Jumpstart text field for custom filter panels.
 */
export const JumpstartFilterText = ({
    label,
    value,
    onChange,
    onEnter,
    minWidth = 140,
    type = 'text',
    fullWidth = false,
    errorText,
    disabled = false,
    autoFocus = false,
    placeholder,
}: JumpstartFilterTextProps) => {
    const safeId = `flt-${label.replace(/\s+/g, '-').toLowerCase()}`;
    const hasError = !!errorText;
    return (
        <Box className="jumpstart-filter" sx={fullWidth ? { width: '100%' } : { minWidth }}>
            <JsTextField
                label={label}
                labelFor={safeId}
                labelId={`${safeId}-label`}
                status={hasError ? 'error' : 'none'}
                errors={hasError ? [errorText as string] : []}
            >
                <input
                    id={safeId}
                    type={type}
                    value={value}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && onEnter) onEnter();
                    }}
                    autoComplete="off"
                />
            </JsTextField>
        </Box>
    );
};

interface JumpstartFilterSelectProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    choices: { id: string; name: string }[];
    minWidth?: number;
    fullWidth?: boolean;
}

/**
 * Standalone controlled Jumpstart select field for custom filter panels.
 */
export const JumpstartFilterSelect = ({
    label,
    value,
    onChange,
    choices,
    minWidth = 140,
    fullWidth = false,
}: JumpstartFilterSelectProps) => {
    const safeId = `flt-${label.replace(/\s+/g, '-').toLowerCase()}`;
    const selectRef = useRef<HTMLSelectElement | null>(null);

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest('select') || target.closest('option')) {
            return;
        }
        e.preventDefault();
        selectRef.current?.focus();
        selectRef.current?.click();
    };

    return (
        <Box
            className="jumpstart-filter"
            sx={fullWidth ? { width: '100%' } : { minWidth }}
            onMouseDown={handleMouseDown}
        >
            <JsSelectField label={label} labelFor={safeId} labelId={`${safeId}-label`}>
                <select
                    ref={selectRef}
                    id={safeId}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                >
                    {choices.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>
            </JsSelectField>
        </Box>
    );
};

/* ====================================================================== */
/* File upload (FileInput replacement)                                    */
/* ====================================================================== */

interface JumpstartFileInputProps {
    source: string;
    label?: string;
    accept?: string;
    disabled?: boolean;
    validate?: any;
    helperText?: string;
    buttonText?: string;
}

/**
 * Jumpstart FileUpload bound to react-admin form state.
 * Stores the selected file as `{ rawFile, title }` to stay compatible with
 * react-admin's FileInput value shape.
 */
export const JumpstartFileInput = (props: JumpstartFileInputProps) => {
    const { label, accept, disabled, helperText, buttonText = 'Select file' } = props;
    const {
        field,
        fieldState: { error, invalid },
        isRequired,
    } = useInput(props);

    const selected = field.value && field.value.rawFile ? (field.value.rawFile as File) : null;

    return (
        <Box sx={wrapSx(true)} className="jumpstart-input jumpstart-file">
            <JsFileUpload
                label={label}
                buttonText={buttonText}
                inputAccept={accept}
                indicator={isRequired ? 'required' : 'none'}
                isDisabled={disabled}
                errors={invalid && error?.message ? [error.message] : []}
                onSelectFiles={({ files }: { files: File[] }) => {
                    const f = files && files[0];
                    field.onChange(f ? { rawFile: f, title: f.name } : undefined);
                }}
            />
            {selected && (
                <Box sx={{ mt: 1, fontSize: '0.875rem', color: 'var(--cg3-color-gray-625, #353535)' }}>
                    Selected: <strong>{selected.name}</strong>
                </Box>
            )}
            {helperText && !invalid && (
                <Box sx={helperSx}>
                    <InputHelperText helperText={helperText} />
                </Box>
            )}
        </Box>
    );
};

/* ====================================================================== */
/* Buttons (Jumpstart ButtonField wrapper)                                */
/* ====================================================================== */
type JsButtonType = 'primary' | 'secondary' | 'tertiary' | 'text' | 'icon';
type JsButtonColor = 'info' | 'danger';
type JsButtonSize = 'small' | 'medium' | 'large';

interface JumpstartButtonProps {
    label?: string;
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    type?: JsButtonType;
    color?: JsButtonColor;
    size?: JsButtonSize;
    nativeType?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
    startIcon?: React.ReactNode;
    flowId?: string;
    sx?: object;
}

/**
 * General-purpose Jumpstart button. Wraps a native <button> in the authentic
 * Jumpstart ButtonField. Supports color 'info' (default) and 'danger' (red).
 */
export const JumpstartButton = ({
    label,
    children,
    onClick,
    type = 'primary',
    color = 'info',
    size = 'medium',
    nativeType = 'button',
    disabled,
    startIcon,
    flowId,
    sx,
}: JumpstartButtonProps) => (
    <Box component="span" sx={{ display: 'inline-flex', ...sx }} className="jumpstart-button">
        <JsButtonField type={type} color={color} size={size}>
            <button type={nativeType} onClick={onClick} disabled={disabled} flow-id={flowId}>
                {startIcon && (
                    <span style={{ display: 'inline-flex', marginRight: label || children ? 6 : 0 }}>
                        {startIcon}
                    </span>
                )}
                {label}
                {children}
            </button>
        </JsButtonField>
    </Box>
);

/**
 * Jumpstart submit button bound to the react-admin form. Renders a native
 * submit button so the surrounding SimpleForm runs its save flow, and reflects
 * the form's submitting state.
 */
export const JumpstartSaveButton = ({
    label = 'Save',
    color = 'info',
}: {
    label?: string;
    color?: JsButtonColor;
}) => {
    const { isSubmitting } = useFormState();
    return (
        <JumpstartButton
            type="primary"
            color={color}
            size="large"
            nativeType="submit"
            disabled={isSubmitting}
            label={isSubmitting ? 'Saving…' : label}
            flowId="save-button"
        />
    );
};

/**
 * Jumpstart toolbar for react-admin SimpleForm. Pass via the `toolbar` prop.
 */
export const JumpstartFormToolbar = ({
    saveLabel = 'Save',
}: {
    saveLabel?: string;
}) => (
    <Box sx={{ mt: 3, pl: 2.5, pb: 2.5, display: 'flex', gap: 1.5 }} className="jumpstart-toolbar">
        <JumpstartSaveButton label={saveLabel} />
    </Box>
);

